import { FormEvent, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/api.service';
import { CustomerValidationsPanel } from '../components/CustomerValidationsPanel';
import { PayslipFileModal } from '../components/PayslipFileModal';
import { usePermissions } from '../context/PermissionsContext';
import { useTenantStore } from '../stores/tenant.store';

const LOAN_STATUS: Record<string, string> = {
  PENDING: 'Pendiente',
  APPROVED: 'Aprobado',
  ACTIVE: 'Activo',
  COMPLETED: 'Finalizado',
  REJECTED: 'Rechazado',
  CANCELLED: 'Cancelado',
  DEFAULTED: 'Mora',
};

const APP_STATUS: Record<string, string> = {
  PENDING: 'Pendiente',
  UNDER_REVIEW: 'En revisión',
  APPROVED: 'Aprobada',
  REJECTED: 'Rechazada',
  CONVERTED: 'Convertida',
  CANCELLED: 'Cancelada',
};

function apiErrorMessage(e: unknown): string {
  const err = e as { response?: { data?: { message?: string | string[] } }; message?: string };
  const msg = err?.response?.data?.message;
  if (Array.isArray(msg)) return msg.join(', ');
  if (typeof msg === 'string') return msg;
  return err?.message ?? 'Error desconocido';
}

function ars(n?: number | null) {
  if (n == null || Number.isNaN(Number(n))) return '—';
  return Number(n).toLocaleString('es-AR', { style: 'currency', currency: 'ARS', maximumFractionDigits: 0 });
}

function fmtDate(d?: string | Date | null) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('es-AR');
}

/** YYYY-MM-DD en zona Argentina (default del campo fecha inicio). */
function todayIsoAr() {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Argentina/Buenos_Aires',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
}

export function NewLoanApplication() {
  const navigate = useNavigate();
  const { session } = usePermissions();
  const legalEntityId = useTenantStore((s) => s.legalEntityId);
  const tenantBranchId = useTenantStore((s) => s.branchId);
  const [query, setQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lookup, setLookup] = useState<any | null>(null);
  const [alta, setAlta] = useState({ firstName: '', lastName: '', phone: '', address: '' });
  const [savingAlta, setSavingAlta] = useState(false);
  const [allBranches, setAllBranches] = useState<any[]>([]);
  const [products, setProducts] = useState<any[]>([]);
  const [referralSources, setReferralSources] = useState<any[]>([]);
  const [publicEntities, setPublicEntities] = useState<any[]>([]);
  const [form, setForm] = useState({
    requestedAmount: '50000',
    requestedTermMonths: '12',
    startDate: todayIsoAr(),
    branchId: '',
    referralSourceId: '',
    productId: '',
    notes: '',
    channelType: 'PRIVATE' as 'PRIVATE' | 'PUBLIC',
    publicEntityId: '',
    employeeFileNumber: '',
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState<string | null>(null);
  const [dniModalOpen, setDniModalOpen] = useState(false);
  const [simulation, setSimulation] = useState<any | null>(null);
  const [simBusy, setSimBusy] = useState(false);
  const [simError, setSimError] = useState<string | null>(null);

  const branches = useMemo(() => {
    let list = allBranches;
    const allowed = session?.allowedBranchIds;
    if (Array.isArray(allowed)) {
      list = list.filter((b) => allowed.includes(b.id));
    }
    if (tenantBranchId) {
      const locked = list.filter((b) => b.id === tenantBranchId);
      if (locked.length) return locked;
    }
    return list;
  }, [allBranches, session?.allowedBranchIds, tenantBranchId]);

  useEffect(() => {
    Promise.all([
      apiService.getBranches(true, legalEntityId || undefined),
      apiService.getLoanProducts({ activeOnly: true }),
      apiService.listReferralSources(),
    ])
      .then(([brs, prods, refs]) => {
        setAllBranches(brs);
        setProducts(prods);
        setReferralSources(refs);
      })
      .catch((e) => setError(apiErrorMessage(e)));
  }, [legalEntityId]);

  useEffect(() => {
    if (!branches.length) {
      if (form.branchId) setForm((f) => ({ ...f, branchId: '' }));
      return;
    }
    const preferred = tenantBranchId && branches.some((b) => b.id === tenantBranchId)
      ? tenantBranchId
      : branches.length === 1
        ? branches[0].id
        : form.branchId && branches.some((b) => b.id === form.branchId)
          ? form.branchId
          : '';
    if (preferred !== form.branchId) {
      setForm((f) => ({ ...f, branchId: preferred }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branches, tenantBranchId]);

  useEffect(() => {
    apiService
      .listPublicEntities(true, form.branchId || undefined)
      .then((ents) => {
        setPublicEntities(ents);
        setForm((f) =>
          f.publicEntityId && !ents.some((e) => e.id === f.publicEntityId)
            ? { ...f, publicEntityId: '' }
            : f,
        );
      })
      .catch(() => setPublicEntities([]));
  }, [form.branchId]);

  const refreshLookup = async (q = query.trim()) => {
    const result = await apiService.lookupLoanApplicationPerson(q);
    setLookup(result);
    return result;
  };

  const search = async (e?: FormEvent) => {
    e?.preventDefault();
    setError(null);
    setSearching(true);
    try {
      await refreshLookup(query.trim());
    } catch (err) {
      setError(apiErrorMessage(err));
      setLookup(null);
    } finally {
      setSearching(false);
    }
  };

  const createCustomer = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!form.branchId) {
      setError('Elegí la sucursal antes de dar de alta al cliente.');
      return;
    }
    setSavingAlta(true);
    try {
      const result = await apiService.ensureLoanApplicationCustomer({
        query: query.trim(),
        firstName: alta.firstName,
        lastName: alta.lastName,
        phone: alta.phone || undefined,
        address: alta.address.trim() || undefined,
        branchId: form.branchId,
      });
      setLookup(result);
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setSavingAlta(false);
    }
  };

  const submitApplication = async (
    decision: 'CONTINUE' | 'BLOCK',
    override?: { validationOverride?: boolean; validationOverrideReason?: string },
  ) => {
    if (!lookup?.found) return;
    let notes = form.notes;
    if (decision === 'BLOCK') {
      const reason = window.prompt('Motivo para bloquear (mín. 10 caracteres):');
      if (!reason || reason.trim().length < 10) return;
      notes = notes ? `${notes}\n${reason.trim()}` : reason.trim();
    }
    setSaving(true);
    setError(null);
    try {
      await apiService.createLoanApplication({
        documentNumber: lookup.customer.documentNumber,
        firstName: lookup.customer.firstName,
        lastName: lookup.customer.lastName,
        phone: lookup.customer.phone ?? undefined,
        requestedAmount: parseFloat(form.requestedAmount),
        requestedTermMonths: parseInt(form.requestedTermMonths, 10),
        startDate: form.startDate || undefined,
        branchId: form.branchId || undefined,
        referralSourceId: form.referralSourceId || undefined,
        productId: form.productId || undefined,
        notes: notes || undefined,
        channelType: form.channelType,
        publicEntityId: form.channelType === 'PUBLIC' ? form.publicEntityId : undefined,
        employeeFileNumber: form.channelType === 'PUBLIC' ? form.employeeFileNumber : undefined,
        decision,
        ...override,
      });
      navigate('/applications');
    } catch (err: any) {
      const data = err?.response?.data;
      if (err?.response?.status === 409 && data?.requiresOverride && decision === 'CONTINUE') {
        const reason = window.prompt(
          `${data.message ?? 'Hay controles en rojo.'}\n\nMotivo para continuar (mín. 10 caracteres):`,
        );
        if (reason && reason.trim().length >= 10) {
          await submitApplication(decision, {
            validationOverride: true,
            validationOverrideReason: reason.trim(),
          });
          return;
        }
      }
      setError(apiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const onUpload = async (kind: 'PAYSLIP' | 'DNI_FRONT' | 'DNI_BACK', file?: File) => {
    if (!file || !lookup?.customer?.id) return;
    setUploading(kind);
    setError(null);
    try {
      const saved = await apiService.uploadOriginationDocument(lookup.customer.id, kind, file);
      if (saved?.id) {
        try {
          await apiService.extractOriginationDocument(saved.id);
        } catch (ocrErr) {
          setError(`Archivo guardado, pero OCR no pudo completarse: ${apiErrorMessage(ocrErr)}`);
        }
      }
      await refreshLookup();
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setUploading(null);
    }
  };

  const confirmDoc = async (doc: any, patch: Record<string, unknown>) => {
    setError(null);
    try {
      await apiService.confirmOriginationDocument(doc.id, patch);
      await refreshLookup();
    } catch (err) {
      setError(apiErrorMessage(err));
    }
  };

  const runSimulation = async () => {
    if (!form.productId || !form.requestedAmount || !form.requestedTermMonths) {
      setSimError('Elegí plan, monto y plazo para simular');
      return;
    }
    setSimBusy(true);
    setSimError(null);
    try {
      const result = await apiService.simulateLoanProduct(form.productId, {
        amount: parseFloat(form.requestedAmount),
        termMonths: parseInt(form.requestedTermMonths, 10),
        startDate: form.startDate || undefined,
        branchId: form.branchId || undefined,
      });
      setSimulation(result);
    } catch (err) {
      setSimulation(null);
      setSimError(apiErrorMessage(err));
    } finally {
      setSimBusy(false);
    }
  };

  const customer = lookup?.found ? lookup.customer : null;
  const blocked = lookup?.found ? lookup.blocked?.blocked : false;
  const payslips = (lookup?.documents ?? []).filter((d: any) => d.kind === 'PAYSLIP');
  const dniFront =
    (lookup?.documents ?? []).find((d: any) => d.kind === 'DNI_FRONT') ||
    (lookup?.documents ?? []).find((d: any) => d.kind === 'DNI') ||
    null;
  const dniBack = (lookup?.documents ?? []).find((d: any) => d.kind === 'DNI_BACK') || null;
  const hasDni = Boolean(dniFront || dniBack);

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-gray-100 flex flex-col">
      <div className="bg-white border-b px-4 py-3 flex flex-col lg:flex-row lg:items-center gap-3">
        <div className="flex items-center justify-between gap-3 lg:min-w-[220px]">
          <h1 className="text-xl font-bold text-gray-900">Nueva presolicitud</h1>
          <button type="button" onClick={() => navigate('/applications')} className="text-sm text-gray-600 hover:underline">
            Volver
          </button>
        </div>
        <form onSubmit={search} className="flex-1 flex gap-2">
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar por DNI o CUIT — trae toda la ficha Credit Now"
            className="flex-1 px-3 py-2 border rounded-md"
            autoFocus
          />
          <button
            type="submit"
            disabled={searching || query.replace(/\D/g, '').length < 6}
            className="px-4 py-2 bg-blue-600 text-white rounded-md disabled:opacity-50"
          >
            {searching ? 'Buscando…' : 'Buscar'}
          </button>
        </form>
      </div>

      {error && <div className="mx-4 mt-3 p-3 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>}

      {lookup && !lookup.found && (
        <form onSubmit={createCustomer} className="m-4 bg-white border rounded-lg p-4 space-y-3">
          <p className="text-sm font-medium text-gray-900">No está en el sistema. Completá el alta para abrir la ficha.</p>
          <div className="grid md:grid-cols-4 gap-3">
            <input required placeholder="Nombre" value={alta.firstName} onChange={(e) => setAlta({ ...alta, firstName: e.target.value })} className="px-3 py-2 border rounded-md" />
            <input required placeholder="Apellido" value={alta.lastName} onChange={(e) => setAlta({ ...alta, lastName: e.target.value })} className="px-3 py-2 border rounded-md" />
            <input placeholder="Teléfono" value={alta.phone} onChange={(e) => setAlta({ ...alta, phone: e.target.value })} className="px-3 py-2 border rounded-md" />
            <input placeholder="Domicilio (opcional)" value={alta.address} onChange={(e) => setAlta({ ...alta, address: e.target.value })} className="px-3 py-2 border rounded-md" />
          </div>
          <select
            required
            value={form.branchId}
            onChange={(e) => setForm({ ...form, branchId: e.target.value })}
            disabled={branches.length <= 1}
            className="w-full max-w-md px-3 py-2 border rounded-md disabled:bg-gray-50"
          >
            <option value="">Sucursal *</option>
            {branches.map((b) => (
              <option key={b.id} value={b.id}>{b.name}</option>
            ))}
          </select>
          {branches.length === 0 && (
            <p className="text-sm text-amber-800">No tenés sucursales habilitadas para el alta.</p>
          )}
          <button type="submit" disabled={savingAlta} className="px-4 py-2 bg-blue-600 text-white rounded-md disabled:opacity-50">
            {savingAlta ? 'Guardando…' : 'Dar de alta y continuar'}
          </button>
        </form>
      )}

      {customer && (
        <div className="flex-1 grid xl:grid-cols-3 gap-3 p-3 min-h-0">
          <section className="bg-white border rounded-lg p-4 overflow-auto space-y-4">
            <div className="flex items-start justify-between gap-2">
              <div>
                <h2 className="text-lg font-semibold">{customer.lastName}, {customer.firstName}</h2>
                <p className="text-sm text-gray-600">DNI {customer.documentNumber}{customer.taxId ? ` · CUIT ${customer.taxId}` : ''}</p>
              </div>
              {(blocked || customer.isBlocked) && (
                <span className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">Bloqueado</span>
              )}
            </div>
            <dl className="grid grid-cols-2 gap-x-3 gap-y-2 text-sm">
              <Info label="Teléfono" value={customer.phone} />
              <Info label="Email" value={customer.email} />
              <Info label="Domicilio" value={[customer.address, customer.city, customer.state].filter(Boolean).join(', ')} />
              <Info label="Nacimiento" value={fmtDate(customer.birthDate)} />
              <Info label="Empleo" value={customer.employmentStatus} />
              <Info label="Antigüedad" value={customer.employmentYears != null ? `${customer.employmentYears} años` : null} />
              <Info label="Ingreso" value={ars(customer.monthlyIncome)} />
              <Info label="Gastos" value={ars(customer.monthlyExpenses)} />
              <Info label="IVA" value={customer.ivaCondition} />
              <Info label="Cliente" value={customer.goodCustomerFlag ? 'Bueno' : '—'} />
            </dl>
            <div>
              <h3 className="text-xs font-medium text-gray-500 uppercase mb-2">Documentos (opcionales)</h3>
              <Dropzone
                label="Recibos de sueldo"
                hint="PDF o foto. 0 o más."
                multiple
                busy={uploading === 'PAYSLIP'}
                onFiles={(files) => files.forEach((f) => onUpload('PAYSLIP', f))}
              />
              <div className="space-y-2 mt-2">
                {payslips.map((doc: any) => (
                  <PayslipConfirm
                    key={doc.id}
                    doc={doc}
                    onSave={confirmDoc}
                    onExtract={async (id) => {
                      setUploading('PAYSLIP');
                      try {
                        await apiService.extractOriginationDocument(id);
                        await refreshLookup();
                      } catch (err) {
                        setError(apiErrorMessage(err));
                      } finally {
                        setUploading(null);
                      }
                    }}
                    extracting={uploading === 'PAYSLIP'}
                  />
                ))}
              </div>
              <div className="mt-3 space-y-2">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <Dropzone
                    label="DNI frente"
                    hint={dniFront ? dniFront.originalName : 'Foto o PDF. Opcional.'}
                    busy={uploading === 'DNI_FRONT'}
                    onFiles={(files) => onUpload('DNI_FRONT', files[0])}
                  />
                  <Dropzone
                    label="DNI dorso"
                    hint={dniBack ? dniBack.originalName : 'Foto o PDF. Opcional.'}
                    busy={uploading === 'DNI_BACK'}
                    onFiles={(files) => onUpload('DNI_BACK', files[0])}
                  />
                </div>
                {hasDni && (
                  <button
                    type="button"
                    className="text-sm text-blue-700 hover:underline"
                    onClick={() => setDniModalOpen(true)}
                  >
                    Ver DNI + OCR
                  </button>
                )}
              </div>
            </div>
          </section>

          <div className="flex flex-col gap-3 min-h-0 overflow-auto">
          <section className="bg-white border rounded-lg p-4 space-y-4">
            {(lookup.warnings ?? []).length > 0 && (
              <div className="space-y-2">
                {lookup.warnings.map((w: { code: string; message: string }) => (
                  <p key={w.code} className="text-sm bg-amber-50 border border-amber-200 text-amber-900 rounded p-2">{w.message}</p>
                ))}
              </div>
            )}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                submitApplication('CONTINUE');
              }}
              className="space-y-3"
            >
              <h3 className="font-semibold">Plan, monto y plazo</h3>
              <select value={form.channelType} onChange={(e) => setForm({ ...form, channelType: e.target.value as 'PRIVATE' | 'PUBLIC' })} className="w-full px-3 py-2 border rounded-md">
                <option value="PRIVATE">Canal privado</option>
                <option value="PUBLIC">Canal público (descuento de haberes)</option>
              </select>
              <select
                value={form.branchId}
                onChange={(e) => setForm({ ...form, branchId: e.target.value })}
                disabled={branches.length <= 1}
                className="w-full px-3 py-2 border rounded-md disabled:bg-gray-50"
              >
                <option value="">Sucursal</option>
                {branches.map((b) => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </select>
              {form.channelType === 'PUBLIC' && (
                <div className="grid grid-cols-2 gap-3">
                  <select required value={form.publicEntityId} onChange={(e) => setForm({ ...form, publicEntityId: e.target.value })} className="px-3 py-2 border rounded-md">
                    <option value="">Entidad pública</option>
                    {publicEntities.map((e) => (
                      <option key={e.id} value={e.id}>{e.code} — {e.name}</option>
                    ))}
                  </select>
                  <input required placeholder="Legajo (ej. 31657/1)" value={form.employeeFileNumber} onChange={(e) => setForm({ ...form, employeeFileNumber: e.target.value })} className="px-3 py-2 border rounded-md" />
                </div>
              )}
              <select value={form.productId} onChange={(e) => { setForm({ ...form, productId: e.target.value }); setSimulation(null); }} className="w-full px-3 py-2 border rounded-md">
                <option value="">Plan</option>
                {products.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}{p.planCode ? ` (${p.planCode})` : ''}</option>
                ))}
              </select>
              <div className="grid grid-cols-2 gap-3">
                <input
                  required
                  type="number"
                  value={form.requestedAmount}
                  onChange={(e) => { setForm({ ...form, requestedAmount: e.target.value }); setSimulation(null); }}
                  placeholder="Monto"
                  className="px-3 py-2 border rounded-md"
                />
                <input
                  type="number"
                  value={form.requestedTermMonths}
                  onChange={(e) => { setForm({ ...form, requestedTermMonths: e.target.value }); setSimulation(null); }}
                  placeholder="Plazo (meses)"
                  className="px-3 py-2 border rounded-md"
                />
              </div>
              <label className="block text-sm text-gray-700">
                Fecha de inicio / otorgamiento
                <input
                  type="date"
                  required
                  value={form.startDate}
                  onChange={(e) => {
                    setForm({ ...form, startDate: e.target.value });
                    setSimulation(null);
                  }}
                  className="mt-1 w-full px-3 py-2 border rounded-md"
                />
                <span className="mt-1 block text-xs text-gray-500">
                  Por defecto hoy. Cambiala para simular y generar cuotas con otra fecha de origen.
                </span>
              </label>
              <button
                type="button"
                disabled={simBusy || !form.productId}
                onClick={runSimulation}
                className="w-full px-3 py-2 border border-blue-300 text-blue-700 rounded-md text-sm hover:bg-blue-50 disabled:opacity-50"
              >
                {simBusy ? 'Calculando…' : 'Calcular cuotas'}
              </button>
              {simError && <p className="text-xs text-red-600">{simError}</p>}
              {simulation?.schedule?.length > 0 && (
                <div className="border rounded-md overflow-hidden">
                  <div className="max-h-48 overflow-auto">
                    <table className="min-w-full text-xs">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr className="text-left text-gray-500">
                          <th className="px-2 py-1">Cuota</th>
                          <th className="px-2 py-1">Fecha venc.</th>
                          <th className="px-2 py-1 text-right">Monto</th>
                        </tr>
                      </thead>
                      <tbody>
                        {simulation.schedule.map((row: any) => (
                          <tr key={row.installmentNumber} className="border-t">
                            <td className="px-2 py-1">{row.installmentNumber}</td>
                            <td className="px-2 py-1">{fmtDate(row.dueDate)}</td>
                            <td className="px-2 py-1 text-right">{ars(row.totalAmount)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="bg-gray-50 border-t px-2 py-2 text-xs space-y-1">
                    <p className="flex justify-between"><span>Cuota aprox.</span><strong>{ars(simulation.pmt)}</strong></p>
                    <p className="flex justify-between"><span>Total a pagar</span><strong>{ars(simulation.totalAmount)}</strong></p>
                    <p className="flex justify-between"><span>Cancelación</span><strong>{fmtDate(simulation.maturityDate)}</strong></p>
                  </div>
                </div>
              )}
              <select value={form.referralSourceId} onChange={(e) => setForm({ ...form, referralSourceId: e.target.value })} className="w-full px-3 py-2 border rounded-md">
                <option value="">Origen / referido</option>
                {referralSources.map((r) => (
                  <option key={r.id} value={r.id}>{r.name}</option>
                ))}
              </select>
              <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} placeholder="Notas" className="w-full px-3 py-2 border rounded-md" />
              <div className="grid grid-cols-2 gap-2">
                <button type="submit" disabled={saving || blocked} className="px-4 py-2 bg-blue-600 text-white rounded-md disabled:opacity-50">
                  {saving ? 'Guardando…' : 'Seguir'}
                </button>
                <button
                  type="button"
                  disabled={saving || blocked}
                  onClick={() => submitApplication('BLOCK')}
                  className="px-4 py-2 border border-red-300 text-red-700 rounded-md disabled:opacity-50"
                >
                  Bloquear
                </button>
              </div>
            </form>
          </section>

          <section className="bg-white border rounded-lg p-4 space-y-4">
            <h3 className="font-semibold">Cartera Credit Now</h3>
            {lookup.loans?.length ? (
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500 text-xs">
                    <th className="py-1 pr-2">Nº</th>
                    <th className="py-1 pr-2">Estado</th>
                    <th className="py-1 pr-2">Saldo</th>
                    <th className="py-1 pr-2">Cuota</th>
                    <th className="py-1">Vence</th>
                  </tr>
                </thead>
                <tbody>
                  {lookup.loans.map((loan: any) => (
                    <tr key={loan.id} className={`border-t ${loan.inArrears ? 'text-red-700' : ''}`}>
                      <td className="py-1 pr-2 font-mono text-xs">{loan.loanNumber ?? loan.id.slice(0, 8)}</td>
                      <td className="py-1 pr-2">{LOAN_STATUS[loan.status] ?? loan.status}{loan.inArrears ? ' · mora' : ''}</td>
                      <td className="py-1 pr-2">{ars(loan.outstanding)}</td>
                      <td className="py-1 pr-2">{ars(loan.installmentAmount)}</td>
                      <td className="py-1">{fmtDate(loan.nextDueDate)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="text-sm text-gray-500">Sin créditos previos.</p>
            )}
            <div>
              <h4 className="text-xs font-medium text-gray-500 uppercase mb-1">Presolicitudes abiertas</h4>
              {lookup.applications?.length ? lookup.applications.map((app: any) => (
                <p key={app.id} className="text-sm">{APP_STATUS[app.status] ?? app.status} · {ars(app.requestedAmount)} · {fmtDate(app.createdAt)}</p>
              )) : <p className="text-sm text-gray-500">Ninguna.</p>}
            </div>
            <div>
              <h4 className="text-xs font-medium text-gray-500 uppercase mb-1">Últimos pagos</h4>
              {lookup.payments?.length ? lookup.payments.map((p: any) => (
                <p key={p.id} className="text-sm">{fmtDate(p.paymentDate)} · {ars(p.amount)} · {p.loan?.loanNumber ?? ''}</p>
              )) : <p className="text-sm text-gray-500">Sin pagos.</p>}
            </div>
          </section>
          </div>

          <section className="bg-white border rounded-lg p-4 overflow-auto">
            <CustomerValidationsPanel customerId={customer.id} autoRun />
          </section>
        </div>
      )}

      {dniModalOpen && hasDni && customer && (
        <DniOcrModal
          customerDocumentNumber={customer.documentNumber}
          front={dniFront}
          back={dniBack}
          onClose={() => setDniModalOpen(false)}
          onConfirm={async (patch) => {
            const target = dniFront || dniBack;
            if (!target) return;
            await confirmDoc(target, patch);
            setDniModalOpen(false);
          }}
          onExtract={async (id) => {
            setUploading('DNI');
            try {
              await apiService.extractOriginationDocument(id);
              await refreshLookup();
            } catch (err) {
              setError(apiErrorMessage(err));
            } finally {
              setUploading(null);
            }
          }}
          extracting={uploading === 'DNI' || uploading === 'DNI_FRONT' || uploading === 'DNI_BACK'}
        />
      )}
    </div>
  );
}

function Info({ label, value }: { label: string; value?: string | null }) {
  return (
    <div>
      <dt className="text-xs text-gray-500">{label}</dt>
      <dd className="text-gray-900 break-words">{value || '—'}</dd>
    </div>
  );
}

function Dropzone({
  label,
  hint,
  multiple,
  busy,
  onFiles,
}: {
  label: string;
  hint: string;
  multiple?: boolean;
  busy?: boolean;
  onFiles: (files: File[]) => void;
}) {
  return (
    <label className="block border border-dashed rounded-md p-3 cursor-pointer hover:bg-gray-50">
      <p className="text-sm font-medium text-gray-800">{label}</p>
      <p className="text-xs text-gray-500">{busy ? 'Subiendo…' : hint}</p>
      <input
        type="file"
        accept="image/jpeg,image/png,image/webp,application/pdf"
        multiple={multiple}
        className="sr-only"
        disabled={busy}
        onChange={(e) => {
          const files = Array.from(e.target.files ?? []);
          e.target.value = '';
          if (files.length) onFiles(files);
        }}
      />
    </label>
  );
}

function PayslipConfirm({
  doc,
  onSave,
  onExtract,
  extracting,
}: {
  doc: any;
  onSave: (doc: any, patch: Record<string, unknown>) => Promise<void>;
  onExtract: (id: string) => Promise<void>;
  extracting?: boolean;
}) {
  const [net, setNet] = useState(doc.confirmedNet != null ? String(doc.confirmedNet) : '');
  const [period, setPeriod] = useState(doc.confirmedPeriod ?? '');
  const [payday, setPayday] = useState(doc.confirmedPayday ?? '');
  const [deductions, setDeductions] = useState(doc.confirmedDeductions ?? '');
  const [viewOpen, setViewOpen] = useState(false);

  useEffect(() => {
    setNet(doc.confirmedNet != null ? String(doc.confirmedNet) : '');
    setPeriod(doc.confirmedPeriod ?? '');
    setPayday(doc.confirmedPayday ?? '');
    setDeductions(doc.confirmedDeductions ?? '');
  }, [doc.id, doc.confirmedNet, doc.confirmedPeriod, doc.confirmedPayday, doc.confirmedDeductions]);

  return (
    <div className="border rounded-md p-2 space-y-2">
      <p className="text-xs text-gray-600">{doc.originalName}</p>
      <div className="grid grid-cols-2 gap-2">
        <input value={net} onChange={(e) => setNet(e.target.value)} placeholder="Neto" className="px-2 py-1 border rounded text-sm" />
        <input value={period} onChange={(e) => setPeriod(e.target.value)} placeholder="Período (mm/aaaa)" className="px-2 py-1 border rounded text-sm" />
        <input value={payday} onChange={(e) => setPayday(e.target.value)} placeholder="Día de cobro" className="px-2 py-1 border rounded text-sm" />
        <input value={deductions} onChange={(e) => setDeductions(e.target.value)} placeholder="Descuentos / otros créditos" className="px-2 py-1 border rounded text-sm" />
      </div>
      <div className="flex flex-wrap gap-3">
        <button
          type="button"
          className="text-xs text-blue-700 hover:underline"
          onClick={() => setViewOpen(true)}
        >
          Ver recibo
        </button>
        <button
          type="button"
          className="text-xs text-blue-700 hover:underline disabled:opacity-50"
          disabled={extracting}
          onClick={() => onExtract(doc.id)}
        >
          {extracting ? 'Extrayendo con IA…' : 'Extraer con IA'}
        </button>
        <button
          type="button"
          className="text-xs text-blue-700 hover:underline"
          onClick={() => onSave(doc, {
            confirmedNet: net ? Number(net) : undefined,
            confirmedPeriod: period || undefined,
            confirmedPayday: payday || undefined,
            confirmedDeductions: deductions || undefined,
          })}
        >
          Confirmar datos del recibo
        </button>
      </div>
      {viewOpen && <PayslipFileModal doc={doc} onClose={() => setViewOpen(false)} />}
    </div>
  );
}

function DniOcrModal({
  customerDocumentNumber,
  front,
  back,
  onClose,
  onConfirm,
  onExtract,
  extracting,
}: {
  customerDocumentNumber?: string;
  front: any;
  back: any;
  onClose: () => void;
  onConfirm: (patch: Record<string, unknown>) => Promise<void>;
  onExtract: (id: string) => Promise<void>;
  extracting?: boolean;
}) {
  const seed = {
    confirmedDniNumber: front?.confirmedDniNumber || back?.confirmedDniNumber || '',
    confirmedFirstName: front?.confirmedFirstName || back?.confirmedFirstName || '',
    confirmedLastName: front?.confirmedLastName || back?.confirmedLastName || '',
    confirmedExpiry: front?.confirmedExpiry || back?.confirmedExpiry || '',
  };
  const [dniNumber, setDniNumber] = useState(seed.confirmedDniNumber);
  const [firstName, setFirstName] = useState(seed.confirmedFirstName);
  const [lastName, setLastName] = useState(seed.confirmedLastName);
  const [expiry, setExpiry] = useState(seed.confirmedExpiry);
  const [frontUrl, setFrontUrl] = useState<string | null>(null);
  const [backUrl, setBackUrl] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setDniNumber(front?.confirmedDniNumber || back?.confirmedDniNumber || '');
    setFirstName(front?.confirmedFirstName || back?.confirmedFirstName || '');
    setLastName(front?.confirmedLastName || back?.confirmedLastName || '');
    setExpiry(front?.confirmedExpiry || back?.confirmedExpiry || '');
  }, [
    front?.id,
    back?.id,
    front?.confirmedDniNumber,
    front?.confirmedFirstName,
    front?.confirmedLastName,
    front?.confirmedExpiry,
    back?.confirmedDniNumber,
    back?.confirmedFirstName,
    back?.confirmedLastName,
    back?.confirmedExpiry,
  ]);

  useEffect(() => {
    let cancelled = false;
    const urls: string[] = [];
    const load = async () => {
      if (front?.id) {
        const blob = await apiService.getOriginationDocumentFileBlob(front.id);
        const url = URL.createObjectURL(blob);
        urls.push(url);
        if (!cancelled) setFrontUrl(url);
      } else if (!cancelled) setFrontUrl(null);
      if (back?.id) {
        const blob = await apiService.getOriginationDocumentFileBlob(back.id);
        const url = URL.createObjectURL(blob);
        urls.push(url);
        if (!cancelled) setBackUrl(url);
      } else if (!cancelled) setBackUrl(null);
    };
    load().catch(() => undefined);
    return () => {
      cancelled = true;
      urls.forEach((u) => URL.revokeObjectURL(u));
    };
  }, [front?.id, back?.id]);

  const mismatch =
    dniNumber &&
    customerDocumentNumber &&
    String(dniNumber).replace(/\D/g, '') !== String(customerDocumentNumber).replace(/\D/g, '');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-auto">
        <div className="flex items-center justify-between border-b px-4 py-3">
          <h2 className="text-lg font-semibold text-gray-900">DNI · fotos y OCR</h2>
          <button type="button" onClick={onClose} className="text-gray-500 hover:text-gray-800 text-sm">
            Cerrar
          </button>
        </div>
        <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-3">
            <DniPreview label="Frente" url={frontUrl} mimeType={front?.mimeType} name={front?.originalName} />
            <DniPreview label="Dorso" url={backUrl} mimeType={back?.mimeType} name={back?.originalName} />
          </div>
          <div className="space-y-3">
            <p className="text-sm text-gray-600">Datos leídos (editables). Confirmá antes de seguir.</p>
            <input
              value={dniNumber}
              onChange={(e) => setDniNumber(e.target.value)}
              placeholder="Nº documento"
              className="w-full px-3 py-2 border rounded-md text-sm"
            />
            <input
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              placeholder="Apellido"
              className="w-full px-3 py-2 border rounded-md text-sm"
            />
            <input
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="Nombre"
              className="w-full px-3 py-2 border rounded-md text-sm"
            />
            <input
              value={expiry}
              onChange={(e) => setExpiry(e.target.value)}
              placeholder="Vencimiento"
              className="w-full px-3 py-2 border rounded-md text-sm"
            />
            {mismatch && (
              <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">
                El Nº del OCR no coincide con el DNI del cliente ({customerDocumentNumber}).
              </p>
            )}
            <div className="flex flex-wrap gap-3 pt-2">
              {front?.id && (
                <button
                  type="button"
                  disabled={extracting}
                  className="text-sm text-blue-700 hover:underline disabled:opacity-50"
                  onClick={() => onExtract(front.id)}
                >
                  Re-OCR frente
                </button>
              )}
              {back?.id && (
                <button
                  type="button"
                  disabled={extracting}
                  className="text-sm text-blue-700 hover:underline disabled:opacity-50"
                  onClick={() => onExtract(back.id)}
                >
                  Re-OCR dorso
                </button>
              )}
              <button
                type="button"
                disabled={saving}
                className="ml-auto px-3 py-2 bg-blue-600 text-white rounded-md text-sm disabled:opacity-50"
                onClick={async () => {
                  setSaving(true);
                  try {
                    await onConfirm({
                      confirmedDniNumber: dniNumber || undefined,
                      confirmedFirstName: firstName || undefined,
                      confirmedLastName: lastName || undefined,
                      confirmedExpiry: expiry || undefined,
                    });
                  } finally {
                    setSaving(false);
                  }
                }}
              >
                Confirmar datos del DNI
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function DniPreview({
  label,
  url,
  mimeType,
  name,
}: {
  label: string;
  url: string | null;
  mimeType?: string;
  name?: string;
}) {
  return (
    <div className="border rounded-md p-2 bg-gray-50">
      <p className="text-xs font-medium text-gray-700 mb-2">{label}{name ? ` · ${name}` : ''}</p>
      {!url ? (
        <p className="text-xs text-gray-500 py-8 text-center">Sin imagen</p>
      ) : mimeType?.startsWith('image/') ? (
        <img src={url} alt={label} className="max-h-56 w-full object-contain rounded" />
      ) : (
        <a href={url} target="_blank" rel="noreferrer" className="text-sm text-blue-700 hover:underline">
          Abrir archivo
        </a>
      )}
    </div>
  );
}

